import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { createApi } from "../api/client";

type Stats = Awaited<ReturnType<ReturnType<typeof createApi>["getAdminStats"]>>;
type User = Awaited<ReturnType<ReturnType<typeof createApi>["listAdminUsers"]>>["users"][number];
type Settings = Awaited<ReturnType<ReturnType<typeof createApi>["getAdminSettings"]>>;

function formatDate(unix: number): string {
  if (!unix) return "—";
  return new Date(unix * 1000).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function Admin() {
  const { isAdmin, loading, getIdToken } = useAuth();
  const api = createApi(getIdToken);

  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<User[] | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busySub, setBusySub] = useState<string | null>(null);
  const [savingModel, setSavingModel] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [s, u, cfg] = await Promise.all([
        api.getAdminStats(),
        api.listAdminUsers(),
        api.getAdminSettings(),
      ]);
      setStats(s);
      setUsers(u.users);
      setSettings(cfg);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Could not load admin data");
    }
  }, [api]);

  const onChangeModel = async (modelId: string) => {
    if (!settings || modelId === settings.video_model) return;
    setSavingModel(true); setError(null);
    try {
      await api.setVideoModel(modelId);
      setSettings({ ...settings, video_model: modelId });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Could not save model");
    } finally {
      setSavingModel(false);
    }
  };

  useEffect(() => { if (isAdmin) refresh(); }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (sub: string, fn: () => Promise<unknown>) => {
    setBusySub(sub); setError(null);
    try {
      await fn();
      await refresh();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusySub(null);
    }
  };

  if (loading) {
    return <section className="mx-auto max-w-6xl px-6 py-16 text-neutral-500">Loading…</section>;
  }

  if (!isAdmin) {
    return (
      <section className="mx-auto max-w-2xl px-6 py-16 text-center">
        <h1 className="font-serif text-4xl tracking-tight">Admin</h1>
        <p className="mt-3 text-neutral-600">
          You're not authorised to view this page.
        </p>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-6xl px-6 py-12">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-4xl tracking-tight">Admin dashboard</h1>
          <p className="mt-2 text-neutral-600">Site statistics and user management.</p>
        </div>
        <button
          onClick={refresh}
          className="text-sm text-neutral-600 hover:text-black transition"
        >
          Refresh
        </button>
      </div>

      {error && (
        <p className="mt-6 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}

      {stats && (
        <>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Users" value={stats.user_count.toLocaleString()} />
            <StatCard label="Jobs (total)" value={stats.job_count.toLocaleString()} />
            <StatCard label="Revenue" value={`$${stats.revenue_usd.toLocaleString()}`} />
            <StatCard label="Jobs · last 24h" value={stats.jobs_last_24h.toLocaleString()} />
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
            {["pending", "paid", "processing", "ready", "failed"].map(s => (
              <StatCard
                key={s}
                label={`Jobs · ${s}`}
                value={(stats.jobs_by_status[s] || 0).toLocaleString()}
                tone={s === "failed" ? "warn" : s === "ready" ? "ok" : "neutral"}
              />
            ))}
          </div>
        </>
      )}

      {settings && (
        <section className="mt-12">
          <h2 className="font-serif text-2xl tracking-tight">Video generation model</h2>
          <p className="mt-2 text-sm text-neutral-600">
            Applies to all new jobs. In-flight jobs keep the model they started with.
          </p>
          <div className="mt-4 rounded-2xl border border-neutral-200 bg-white p-5 space-y-3">
            {settings.available_models.map(m => (
              <label
                key={m.id}
                className={`flex items-start gap-3 cursor-pointer rounded-xl border p-3 transition ${
                  settings.video_model === m.id
                    ? "border-neutral-900 bg-neutral-50"
                    : "border-neutral-200 hover:border-neutral-300"
                } ${savingModel ? "opacity-60 pointer-events-none" : ""}`}
              >
                <input
                  type="radio"
                  name="video_model"
                  value={m.id}
                  checked={settings.video_model === m.id}
                  onChange={() => onChangeModel(m.id)}
                  className="mt-1"
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-neutral-900">{m.label}</span>
                    <span
                      className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                        m.audio
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-neutral-100 text-neutral-500"
                      }`}
                    >
                      {m.audio ? "with audio" : "silent"}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-neutral-500">{m.description}</p>
                </div>
              </label>
            ))}
          </div>
        </section>
      )}

      {users && (
        <section className="mt-12">
          <h2 className="font-serif text-2xl tracking-tight">Users</h2>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-neutral-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-600">
                <tr>
                  <Th>Email</Th>
                  <Th>Status</Th>
                  <Th>Signed up</Th>
                  <Th className="text-right">Jobs</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {users.map(u => (
                  <tr key={u.sub} className={u.enabled ? "" : "opacity-50"}>
                    <Td>
                      <div className="font-medium text-neutral-900">{u.email || "(no email)"}</div>
                      <div className="text-xs text-neutral-400 font-mono">{u.sub}</div>
                    </Td>
                    <Td>
                      <span className={`text-xs uppercase tracking-wider ${
                        u.enabled ? "text-neutral-700" : "text-red-600"
                      }`}>
                        {u.enabled ? u.status : "disabled"}
                      </span>
                    </Td>
                    <Td className="whitespace-nowrap text-neutral-500">{formatDate(u.created_at)}</Td>
                    <Td className="text-right text-neutral-700">{u.job_count}</Td>
                    <Td className="text-right whitespace-nowrap">
                      <div className="flex items-center gap-3 justify-end">
                        {u.enabled ? (
                          <button
                            onClick={() => act(u.sub, () => api.disableUser(u.sub))}
                            disabled={busySub === u.sub}
                            className="text-xs font-medium text-neutral-600 hover:text-black disabled:opacity-50"
                          >
                            Disable
                          </button>
                        ) : (
                          <button
                            onClick={() => act(u.sub, () => api.enableUser(u.sub))}
                            disabled={busySub === u.sub}
                            className="text-xs font-medium text-neutral-600 hover:text-black disabled:opacity-50"
                          >
                            Enable
                          </button>
                        )}
                        <button
                          onClick={() => {
                            const ok = window.confirm(
                              `Permanently delete ${u.email}?\n\nTheir account, ${u.job_count} job(s), and all their photos/videos will be removed. This cannot be undone.`
                            );
                            if (ok) act(u.sub, () => api.deleteUser(u.sub));
                          }}
                          disabled={busySub === u.sub}
                          className="text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><Td>(no users yet)</Td><Td /><Td /><Td /><Td /></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {stats?.recent_jobs && stats.recent_jobs.length > 0 && (
        <section className="mt-12">
          <h2 className="font-serif text-2xl tracking-tight">Recent jobs</h2>
          <ul className="mt-4 divide-y divide-neutral-200 rounded-2xl border border-neutral-200 bg-white">
            {stats.recent_jobs.map(j => (
              <li key={j.job_id} className="px-4 py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm text-neutral-800 truncate">
                    {j.subject_hint || <span className="italic text-neutral-400">Untitled</span>}
                  </p>
                  <p className="text-xs text-neutral-400 font-mono truncate">{j.job_id}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs uppercase tracking-wider text-neutral-500">{j.status}</p>
                  <p className="text-xs text-neutral-400">{formatDate(j.created_at)}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </section>
  );
}

function StatCard({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "neutral" | "ok" | "warn";
}) {
  const toneClass =
    tone === "ok"
      ? "text-emerald-700"
      : tone === "warn"
      ? "text-red-700"
      : "text-neutral-900";
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white px-4 py-3">
      <p className="text-xs uppercase tracking-wider text-neutral-500">{label}</p>
      <p className={`mt-1 font-serif text-2xl ${toneClass}`}>{value}</p>
    </div>
  );
}

function Th({ children, className = "" }: { children?: React.ReactNode; className?: string }) {
  return (
    <th className={`text-left text-xs uppercase tracking-wider font-medium px-4 py-3 ${className}`}>
      {children}
    </th>
  );
}

function Td({ children, className = "" }: { children?: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}
